import {
  useForm,
  useFieldArray,
  Controller,
  FieldErrors,
} from "react-hook-form";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Building2,
  CheckCircle,
  CircleAlert,
  Loader2,
  Mail,
  Phone,
  Plus,
  Search,
  Settings,
  Trash2,
} from "lucide-react";
import { DocumentType } from "@/types/form";
import { getValidationSchema } from "@/schema/documentSchema";
import { zodResolver } from "@hookform/resolvers/zod";
import type { z, ZodTypeAny } from "zod";
import { SearchModal } from "./modal-item";
import { useEffect, useMemo, useState } from "react";
import {
  LineItemForm,
  type buyerSchema,
  type invoiceItemSchema,
  type supplierSchema,
} from "@/schema/schema";
import { useLocation, useParams } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { Box, Tabs } from "@radix-ui/themes";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { registrationTypes } from "@/constants/registrationTypes";
import { countries } from "@/constants/countryCodes";
import { states } from "@/constants/states";
import { paymentMode } from "@/constants/paymentMode";
import { frequencyOfBilling } from "@/constants/frequencyOfBilling";
import { classificationCodes } from "@/constants/classificationCodes";
import { UomCombobox } from "@/components/uom-combobox";
import { taxTypes } from "@/constants/taxTypes";
import SearchBuyerModal from "./modal-buyer";
import {
  deliveryOrdersApi,
  salesOrdersApi,
} from "@/services/salesDocumentsApi";
import { DeliveryOrderPickerModal } from "@/components/modals/DeliveryOrderPickerModal";
import { SalesOrderPickerModal } from "@/components/modals/SalesOrderPickerModal";
import { format } from "date-fns";
import { useInvoicePrefix } from "@/hooks/useInvoicePrefix";
import SearchSupplierModal from "./modal-supplier";
import { useJobCode } from "@/hooks/userJobCode";
import { useCurrentActiveBranch } from "@/hooks/useCurrentActiveBranch";
import { useToast } from "@/hooks/use-toast";
import { useDecimalPrecision } from "@/hooks/useDecimalPrecision";
import { RATE_DISPLAY_PRECISION, RATE_INPUT_STEP, roundTo } from "@/utils/decimal";
import { deriveDocumentTotals } from "@/utils/documentTotals";
import SearchInvoiceModal, { InvoiceList } from "./modal-invoice";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/utils/axiosInterceptor";
import {
  allocateNetByTaxType,
  round2,
  type ConsolidateTaxEntry,
} from "@/utils/consolidationTax";
import { currencyCodes } from "@/constants/currencyCodes";
import IncompleteProfile from "./incomplete-profile";
import LoadingIndicator from "./LoadingIndicator";
import { UserInfo } from "@/types/auth";
import TinSearch from "./modal-tin";
import SearchInvoiceReferenceNumber from "./search-invoice-reference";
import { EInvoice } from "@/types/e-invoice";
import { lineItemDefaultValue } from "@/constants/lineItemDefault";
import InsertEInvoiceReference from "./insert-einvoice-reference";
import { useInvoices } from "@/hooks/useInvoices";

// LHDN caps each UBL AddressLine
const ADDRESS_LINE_MAX = 150;

const AddressCharCount = ({ value }: { value?: string | null }) => {
  const length = value?.length ?? 0;
  const remaining = ADDRESS_LINE_MAX - length;
  return (
    <p
      className={`text-xs mt-1 ${
        remaining < 0
          ? "text-red-600"
          : remaining <= 20
            ? "text-amber-600"
            : "text-muted-foreground"
      }`}
    >
      {length}/{ADDRESS_LINE_MAX}
      {remaining < 0 && " — too long, move the rest to the next address line"}
    </p>
  );
};

const EInvoiceForm = () => {
  const { user } = useAuth();
  if (!user) return <LoadingIndicator />;
  if (user.addressId === null) return <IncompleteProfile />;

  return <DocumentView user={user} />;
};

type DocumentViewProps = {
  user: UserInfo;
};

const DocumentView = ({ user }: DocumentViewProps) => {
  const { toast } = useToast();
  const { round, step, roundTotal, formatTotalAmount } = useDecimalPrecision();
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isSearchBuyerModalOpen, setIsSearchBuyerModalOpen] = useState(false);
  const [isSearchSupplierModalOpen, setIsSearchSupplierModalOpen] =
    useState(false);
  const [supplierCode, setSupplierCode] = useState<string | null>(null);
  const [buyerCode, setBuyerCode] = useState<string | null>(null);
  const [totals, setTotals] = useState({
    totalExcludingTax: 0,
    totalIncludingTax: 0,
    totalTaxAmount: 0,
    totalPayableAmount: 0,
  });
  const { jobCode } = useJobCode();
  const { currentActiveBranch } = useCurrentActiveBranch();
  const params = useParams();
  const [_, navigate] = useLocation();
  const { createInvoiceAsync, isCreatingInvoice } = useInvoices();
  const documentType = params.documentType as DocumentType;
  const { codeNumberOptions } = useInvoicePrefix(documentType);
  const consolidatedInvoiceDefaultValue =
    getBuyerIdForConsolidatedInvoice(documentType);
  const [openTinSearch, setOpenTinSearch] = useState<boolean>(false);
  const [openEInvoice, setOpenEInvoice] = useState<boolean>(false);
  const [currencySearch, setCurrencySearch] = useState("");
  const [openInsertReference, setOpenInsertReference] =
    useState<boolean>(false);
  const [newLineItem, setNewLineItem] =
    useState<LineItemForm>(lineItemDefaultValue);
  const [invoiceReferenceSubstitution, setInvoiceReferenceSubstitution] =
    useState<string | undefined>(undefined);
  const [isDeliveryOrderPickerOpen, setIsDeliveryOrderPickerOpen] =
    useState(false);
  const [selectedDeliveryOrderNumber, setSelectedDeliveryOrderNumber] =
    useState<string | null>(null);
  const [selectedDeliveryOrderId, setSelectedDeliveryOrderId] = useState<
    string | null
  >(null);
  const [isSalesOrderPickerOpen, setIsSalesOrderPickerOpen] = useState(false);
  const [selectedSalesOrderNumber, setSelectedSalesOrderNumber] = useState<
    string | null
  >(null);

  const { zodSchema, defaultValues } = useMemo(() => {
    const commonFieldsValue = {
      // Invoice Details
      documentType: documentType, // Should match `DocumentType` enum
      codeNumber: "",
      invoiceDate: format(new Date(), "yyyy-MM-dd"), // Zod expects string
      invoiceTime: format(new Date(), "HH:mm:ss"), // Zod expects string
      issuerDigitalSignature: "",
      currencyCode: "MYR",
      currencyExchangeRate: 1.0, // Nullable number
      docDate: null,
      jobCode: null,
      originalInvoiceNumber: undefined,

      // Frequency of Billing Information
      frequencyOfBilling: null,
      billingPeriodStartDate: null,
      billingPeriodEndDate: null,

      // Invoice Line Items
      items: [], // Array of `invoiceItemSchema`

      // Payment & Prepayment Information
      paymentMode: null, // Nullable enum
      supplierBankAccountNumber: null, // Nullable number
      paymentTerms: null,
      prepaymentAmount: null, // Nullable number
      prepaymentDate: null,
      prepaymentReferenceNumber: null,
      billReferenceNumber: null,

      //Shipping Information
      shipping: undefined,

      // Import & Export Information
      customs: undefined,
    };

    // when issue non self-billed invoice (01, 02, 03, 04)
    const companyAsSupplierDefaultValue = {
      ...commonFieldsValue,
      // Buyer Information
      buyerName: "",
      buyerTin: "",
      buyerRegistrationType: "",
      buyerRegistrationNumber: "",
      buyerSstRegistrationNumber: "",
      buyerEmail: "",
      buyerContact: "",
      buyerAddressLine0: "",
      buyerAddressLine1: null,
      buyerAddressLine2: null,
      buyerCity: "",
      buyerPostcode: null,
      buyerCountry: "",
      buyerState: "",

      // Supplier Information
      supplierName: user.businessName,
      supplierTin: user.tin,
      supplierRegistrationType: user.registrationType,
      supplierRegistrationNumber: user.registrationNumber,
      supplierCode: "DEFAULT", // Set default supplier code
      supplierSstRegistrationNumber: user.sstRegistrationNumber ?? null, // Optional
      supplierMsicCode: user.msicCode,
      supplierBusinessActivityDescription: user.businessActivityDescription,
      supplierTourismTaxRegistrationNumber: user.tourismTaxRegistrationNumber,
      supplierEmail: user.email ?? null,
      supplierContact: user.contactNumber,
      supplierAddressLine0: user.addressLine0,
      supplierAddressLine1: user.addressLine1 ?? null,
      supplierAddressLine2: user.addressLine2 ?? null,
      supplierCity: user.cityName,
      supplierPostcode: user.postalZone ?? null,
      supplierCountry: user.country,
      supplierState: user.state,
    };

    // when issue self-billed invoice (11, 12, 13, 14)
    const companyAsBuyerDefaultValue = {
      ...commonFieldsValue,
      // Buyer Information
      buyerName: user.businessName,
      buyerTin: user.tin,
      buyerRegistrationType: user.registrationType,
      buyerRegistrationNumber: user.registrationNumber,
      buyerSstRegistrationNumber: user.sstRegistrationNumber,
      buyerEmail: user.email ?? null,
      buyerContact: user.contactNumber,
      buyerAddressLine0: user.addressLine0,
      buyerAddressLine1: user.addressLine1 ?? null,
      buyerAddressLine2: user.addressLine2 ?? null,
      buyerCity: user.cityName,
      buyerPostcode: user.postalZone ?? null,
      buyerCountry: user.country,
      buyerState: user.state,

      // Supplier Information
      supplierName: "",
      supplierTin: "",
      supplierRegistrationType: "",
      supplierRegistrationNumber: "",
      supplierSstRegistrationNumber: null, // Optional
      supplierMsicCode: "",
      supplierBusinessActivityDescription: "",
      supplierTourismTaxRegistrationNumber: "",
      supplierEmail: "",
      supplierContact: "",
      supplierAddressLine0: "",
      supplierAddressLine1: null,
      supplierAddressLine2: null,
      supplierCity: "",
      supplierPostcode: null,
      supplierCountry: "",
      supplierState: "",
    };

    const docType: DocumentType =
      documentType === DocumentType.CONSOLIDATED_INVOICE
        ? DocumentType.INVOICE
        : documentType; //change 01A to 01 to get the schema

    return {
      zodSchema: getValidationSchema(docType),
      defaultValues: ["11", "12", "13", "14"].includes(docType)
        ? companyAsBuyerDefaultValue
        : companyAsSupplierDefaultValue,
    };
  }, [documentType, user]);

  if (!zodSchema) {
    return <div>Loading...</div>;
  }

  type FormValues = typeof zodSchema extends ZodTypeAny
    ? z.infer<typeof zodSchema>
    : unknown;

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(zodSchema),
    defaultValues,
  });

  const draftKey = `invoice-draft-${documentType}-${user.tin}`;

  useEffect(() => {
    sessionStorage.removeItem(draftKey);

    reset(defaultValues);
  }, [documentType, user.tin]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "items",
  });

  const [items = []] = watch(["items"]);
  const buyerTin = watch("buyerTin");
  const supplierTin = watch("supplierTin");
  const invoiceAdditionalDiscountAmount =
    watch("invoiceAdditionalDiscountAmount") ?? 0;
  const invoiceAdditionalFeeAmount = watch("invoiceAdditionalFeeAmount") ?? 0;
  const roundingAmount = watch("roundingAmount") ?? 0;
  const currencyCode = watch("currencyCode");

  const recalculateTotals = () => {
    const items = getValues("items") || [];
    const invoiceAdditionalDiscountAmount =
      getValues("invoiceAdditionalDiscountAmount") ?? 0;
    const invoiceAdditionalFeeAmount =
      getValues("invoiceAdditionalFeeAmount") ?? 0;
    const roundingAmount = getValues("roundingAmount") ?? 0;

    const rawExcludingTax = items.reduce(
      (sum: number, item: z.infer<typeof invoiceItemSchema>) =>
        sum + (Number(item.totalExcludingTax) || 0),
      0,
    );

    // Per tax type, rounded once each, then summed — matching taxTotals.ts so
    // TaxTotal reconciles against its TaxSubtotal entries.
    const totalTaxAmount = calculateTotalTaxPerType().reduce(
      (sum, entry) => sum + entry.taxAmount,
      0,
    );

    setTotals(
      deriveDocumentTotals({
        rawExcludingTax,
        totalTaxAmount,
        additionalDiscountAmount: invoiceAdditionalDiscountAmount,
        additionalFeeAmount: invoiceAdditionalFeeAmount,
        roundingAmount,
      }),
    );
  };

  const calculateTotalTaxPerType = () => {
    const items = getValues("items") || [];

    const map = new Map<
      "01" | "02" | "03" | "04" | "05" | "06" | "E",
      number
    >();

    for (const item of items) {
      if (!item || item._delete) continue;

      const taxType = item.taxType;
      const taxAmount = Number(item.taxAmount ?? 0);

      if (!taxType) continue;

      map.set(taxType, (map.get(taxType) || 0) + taxAmount);
    }

    const grouped = Array.from(map.entries()).map(([taxType, taxAmount]) => ({
      taxType,
      taxAmount: roundTotal(taxAmount),
    }));

    return grouped;
  };

  const handleCalculation = (
    index: number,
    field: string,
    value: string | number,
  ) => {
    const items = getValues("items");
    const item = items[index];

    if (!item) return;
    const updatedItem = {
      ...item,
      [field]: Number(value) || 0,
    };
    const toSafeNumber = (value: any): number => {
      const num = Number(value);
      return isNaN(num) ? 0 : num;
    };

    const quantity = toSafeNumber(updatedItem.quantity);
    const unitPrice = toSafeNumber(updatedItem.unitPrice);
    const discountRate = toSafeNumber(updatedItem.discountRate);
    const feeRate = toSafeNumber(updatedItem.feeRate);

    const subtotal = quantity * unitPrice;
    const discountAmount = subtotal * (discountRate / 100);

    // According to Mr Ng, we should follow customs
    // service charge are not included in taxable amount
    const taxableAmount = subtotal - discountAmount;

    // use taxable amount due to is the same for charge
    const feeChargeAmount = taxableAmount * (feeRate / 100);

    let taxAmount, taxRate;

    // Check if this item has editable tax amount
    // If isEditable = 1, preserve the original taxAmount instead of recalculating
    const hasEditableTaxAmount = (item as any).isEditable === 1;

    if (field === "taxAmount") {
      // If tax amount is manually entered and editable
      taxAmount = Number(value);
      taxRate = taxableAmount > 0 ? (taxAmount / taxableAmount) * 100 : 0;
    } else if (hasEditableTaxAmount && field !== "taxRate") {
      // For items with fixed tax amounts, preserve the original taxAmount
      taxAmount = toSafeNumber(item.taxAmount);
      taxRate = taxableAmount > 0 ? (taxAmount / taxableAmount) * 100 : 0;
    } else {
      // Normal calculation using tax rate
      taxRate = Number(updatedItem.taxRate ?? 0);
      taxAmount = taxableAmount * (taxRate / 100);
    }

    const totalExcludingTax = taxableAmount + feeChargeAmount;
    const total = totalExcludingTax + taxAmount; // also known as total after tax

    setValue(`items.${index}.subtotal`, round(subtotal), {
      shouldValidate: true,
    });
    setValue(
      `items.${index}.discountAmount`,
      round(discountAmount),
      { shouldValidate: true },
    );
    setValue(
      `items.${index}.feeAmount`,
      round(feeChargeAmount),
      { shouldValidate: true },
    );
    setValue(`items.${index}.taxRate`, roundTo(taxRate, RATE_DISPLAY_PRECISION), {
      shouldValidate: true,
    });
    setValue(`items.${index}.taxAmount`, round(taxAmount), {
      shouldValidate: true,
    });
    setValue(
      `items.${index}.totalExcludingTax`,
      round(totalExcludingTax),
      { shouldValidate: true },
    );
    setValue(`items.${index}.total`, round(total));

    // Recalculate invoice-level totals after updating line item
    recalculateTotals();
  };

  const DocumentTypeLabels: Record<string, string> = {
    "01": "Invoice",
    "02": "Credit Note",
    "03": "Debit Note",
    "04": "Refund Note",
    "11": "Self-Billed Invoice",
    "12": "Self-Billed Credit Note",
    "13": "Self-Billed Debit Note",
    "14": "Self-Billed Refund Note",
    "01A": "Consolidated Invoice",
  };

  //default fields value if document type is consolidated invoice
  useEffect(() => {
    if (documentType === DocumentType.CONSOLIDATED_INVOICE) {
      if (consolidatedInvoiceDefaultValue) {
        setValue("buyerRegistrationType", "BRN");
        setValue(
          "buyerRegistrationNumber",
          consolidatedInvoiceDefaultValue.registrationNumber,
        );
        setValue("buyerTin", consolidatedInvoiceDefaultValue.tin);
        setValue("buyerName", consolidatedInvoiceDefaultValue.name);
        setValue(
          "buyerSstRegistrationNumber",
          consolidatedInvoiceDefaultValue.sstRegistrationNumber,
        );
        setValue("buyerEmail", "NA");
        setValue("buyerContact", consolidatedInvoiceDefaultValue.contactNumber);
        setValue(
          "buyerAddressLine0",
          consolidatedInvoiceDefaultValue.buyerAddress.addressLine0,
        );
        setValue(
          "buyerAddressLine1",
          consolidatedInvoiceDefaultValue.buyerAddress.addressLine1,
        );
        setValue(
          "buyerAddressLine2",
          consolidatedInvoiceDefaultValue.buyerAddress.addressLine2,
        );
        setValue(
          "buyerCity",
          consolidatedInvoiceDefaultValue.buyerAddress.cityName,
        );
        setValue(
          "buyerPostcode",
          consolidatedInvoiceDefaultValue.buyerAddress.postalZone,
        );
        setValue(
          "buyerState",
          consolidatedInvoiceDefaultValue.buyerAddress.state,
        );
        setValue(
          "buyerCountry",
          consolidatedInvoiceDefaultValue.buyerAddress.country,
        );
      }
    }
  }, [consolidatedInvoiceDefaultValue, documentType]);

  //dropdown options for code number @ default prefix is selected if exist
  useEffect(() => {
    if (codeNumberOptions) {
      setValue(
        "codeNumber",
        codeNumberOptions.find((option) => option.isDefault === true)!
          .codeNumber,
      );
    }
  }, [codeNumberOptions, documentType]);

  useEffect(() => {
    const rawExcludingTax = items.reduce(
      (sum: number, item: z.infer<typeof invoiceItemSchema>) =>
        sum + (Number(item.totalExcludingTax) || 0),
      0,
    );

    const totalTaxAmount = calculateTotalTaxPerType().reduce(
      (sum, entry) => sum + entry.taxAmount,
      0,
    );

    // based on lhdn guidelines, the rounding should be added
    setTotals(
      deriveDocumentTotals({
        rawExcludingTax,
        totalTaxAmount,
        additionalDiscountAmount: invoiceAdditionalDiscountAmount,
        additionalFeeAmount: invoiceAdditionalFeeAmount,
        roundingAmount,
      }),
    );
  }, [
    items,
    invoiceAdditionalDiscountAmount,
    invoiceAdditionalFeeAmount,
    roundingAmount,
  ]);

  const onSubmit = async (data: FormValues) => {
    // Removed validation: "one UUID one line item" restriction
    // Credit Notes (02, 03, 04) and Self-Billed Credit Notes (12, 13, 14)
    // can now have multiple line items per UUID to match the original invoice structure

    // Stock validation for Regular Invoice (Type 01)
    if (documentType === "01") {
      const insufficientStockItems: string[] = [];

      for (const item of items) {
        // Only validate stock items (check if item has stock tracking)
        if ((item as any).stockType === "stock") {
          const availableQty = (item as any).availableQuantity || 0;
          const requestedQty = Number(item.quantity);

          if (availableQty < requestedQty) {
            insufficientStockItems.push(
              `${item.codeNumber}: available ${availableQty}, requested ${requestedQty}`,
            );
          }
        }
      }

      if (insufficientStockItems.length > 0) {
        toast({
          title: "Insufficient Stock",
          variant: "destructive",
          description: (
            <div className="space-y-1">
              <p>The following items have insufficient stock:</p>
              <ul className="list-disc list-inside">
                {insufficientStockItems.map((msg, idx) => (
                  <li key={idx} className="text-sm">
                    {msg}
                  </li>
                ))}
              </ul>
            </div>
          ),
          icon: <CircleAlert size={30} className="animate-spin-once" />,
          duration: 6000,
        });
        return null;
      }
    }

    const lineItems = items.map((item: z.infer<typeof invoiceItemSchema>) => ({
      itemCode: item.codeNumber, // Add itemCode field for backend compatibility
      classification: item.classification,
      description: item.description,
      // due to form receive all input as string, convert the
      // string to number again to match api schema
      unitPrice: Number(item.unitPrice),
      taxType: item.taxType,
      taxRate: Number(item.taxRate),
      taxAmount: Number(item.taxAmount),
      detailsOfTaxExemption: item.detailsOfTaxExemption,
      amountExemptedFromTax: item.amountExemptedFromTax,
      subtotal: item.subtotal,
      totalExcludingTax: item.totalExcludingTax,
      quantity: Number(item.quantity),
      measurement: item.measurement,
      discountRate: Number(item.discountRate),
      discountAmount: item.discountAmount,
      feeRate: Number(item.feeRate),
      feeAmount: item.feeAmount,
      productTariffCode: item.productTariffCode,
      countryOfOrigin: item.countryOfOrigin,
    }));

    const invoiceDataToBeSave = {
      // supplier information
      supplierCode: supplierCode,
      supplierRegistrationType: data.supplierRegistrationType,
      supplierRegistrationNumber: data.supplierRegistrationNumber,
      supplierTin: data.supplierTin,
      supplierName: data.supplierName,
      supplierSstRegistrationNumber: data.supplierSstRegistrationNumber,
      supplierMsicCode: data.supplierMsicCode,
      supplierTourismTaxRegistrationNumber:
        data.supplierTourismTaxRegistrationNumber,
      supplierBusinessActivityDescription:
        data.supplierBusinessActivityDescription,
      supplierEmail: data.supplierEmail,
      supplierContact: data.supplierContact,
      supplierAddressLine0: data.supplierAddressLine0,
      supplierAddressLine1: data.supplierAddressLine1,
      supplierAddressLine2: data.supplierAddressLine2,
      supplierCity: data.supplierCity,
      supplierPostcode: data.supplierPostcode,
      supplierState: data.supplierState,
      supplierCountry: data.supplierCountry,

      // buyer information
      buyerCode: buyerCode,
      buyerRegistrationType: data.buyerRegistrationType,
      buyerRegistrationNumber: data.buyerRegistrationNumber,
      buyerTin: data.buyerTin,
      buyerName: data.buyerName,
      buyerSstRegistrationNumber: data.buyerSstRegistrationNumber,
      buyerEmail: data.buyerEmail,
      buyerContact: data.buyerContact,
      buyerAddressLine0: data.buyerAddressLine0,
      buyerAddressLine1: data.buyerAddressLine1,
      buyerAddressLine2: data.buyerAddressLine2,
      buyerCity: data.buyerCity,
      buyerPostcode: data.buyerPostcode,
      buyerState: data.buyerState,
      buyerCountry: data.buyerCountry,

      // invoice details
      version: "1.0", // Max 5 characters
      typeCode: documentType, // Enum, limited to 2 characters
      originalInvoiceNumber: data.originalInvoiceNumber,
      originalInvoiceReference: data.originalInvoiceReference,
      codeNumber: data.codeNumber, // Max 50 characters
      invoiceDate: new Date(), // Transformed from string datetime
      invoiceTime: new Date(), // Transformed from string datetime
      issuerDigitalSignature: "123456789abceds",
      currencyCode: data.currencyCode, // Max 3 characters
      currencyExchangeRate: Number(data.currencyExchangeRate), // Default 1 if not provided
      docDate: data.docDate ? new Date(data.docDate) : null, // Transformed from string datetime
      jobCode: data.jobCode,

      // Source document reference for status updates
      sourceDeliveryOrderId: selectedDeliveryOrderId,

      // Frequent of Billing Information
      frequencyOfBilling: data.frequencyOfBilling,
      billingPeriodStartDate: data.billingPeriodStartDate
        ? new Date(data.billingPeriodStartDate)
        : null,
      billingPeriodEndDate: data.billingPeriodEndDate
        ? new Date(data.billingPeriodEndDate)
        : null,

      // invoice line item
      lineItems: lineItems, // Must have at least 1 line item

      // Payment and Prepayment Information
      paymentMode: data.paymentMode,
      supplierBankAccountNumber: data.supplierBankAccountNumber, // Max 150 characters
      paymentTerms: data.paymentTerms, // Max 300 characters
      prepaymentAmount: data.prepaymentAmount, // Default 0
      prepaymentDate: data.prepaymentDate
        ? new Date(data.prepaymentDate)
        : null,
      prepaymentTime: null,
      prepaymentReferenceNumber: data.prepaymentReferenceNumber, // Max 150 characters
      billReferenceNumber: data.billReferenceNumber, // Max 150 characters

      //shipping info
      shipping: data.shipping,

      //imports and exports
      customs: data.customs,

      // LegalMonetaryTotal
      totalExcludingTax: roundTotal(totals.totalExcludingTax),
      totalIncludingTax: roundTotal(totals.totalIncludingTax),
      totalPayableAmount: roundTotal(totals.totalPayableAmount),
      totalNetAmount: null, // Default 0
      totalDiscountValue: null, // Default 0
      totalFeeChargeAmount: null, // Default 0

      // taxTotal
      totalTaxAmount: roundTotal(totals.totalTaxAmount),
      roundingAmount: data.roundingAmount, // Default 0
      totalTaxableAmountPerTaxType: null, // Default 0
      totalTaxAmountPerTaxType: calculateTotalTaxPerType(),
      taxType: "06" as const, // Default to TaxCode.NotApplicable

      // AllowanceCharge
      invoiceAdditionalDiscountAmount: data.invoiceAdditionalDiscountAmount, // Default 0
      invoiceAdditionalFeeAmount: data.invoiceAdditionalFeeAmount, // Default 0
    };

    try {
      const prefixId = codeNumberOptions?.find(
        (option) => option.codeNumber === getValues("codeNumber"),
      );
      const data = {
        invoices: invoiceDataToBeSave,
        isConsolidatedInvoice: documentType === "01A" ? true : false,
        prefixId: prefixId?.id,
      };
      await createInvoiceAsync(data, {
        onSuccess: () => {
          toast({
            title: "Invoice Saved",
            description: `The invoice ${getValues(
              "codeNumber",
            )} successfully saved.`,
            duration: 5000,
            icon: (
              <CheckCircle
                size={30}
                className="animate-spin-once text-green-400"
              />
            ),
          });
          navigate("/invoices");
        },
        onError: (error: any) => {
          const errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            "Failed to save invoice. Please try again.";

          toast({
            title: "Failed",
            variant: "destructive",
            description: errorMessage,
            icon: <CircleAlert size={30} className="animate-spin-once" />,
            duration: 4000,
          });
        },
      });
    } catch (error) {
      console.error("Error creating invoice:", error);
    }
  };

  const onError = (errors: FieldErrors<FormValues>) => {
    // Find the first error field and show a specific message
    const firstError = Object.entries(errors)[0];
    const fieldName = firstError?.[0];
    const errorMessage = firstError?.[1]?.message as string;

    const description = errorMessage || "Please fill in all required fields";

    console.error("Form validation errors:", errors);
    console.error("First error field:", fieldName);

    // Log detailed items validation errors
    if (errors.items && Array.isArray(errors.items)) {
      console.error("Items validation errors:", errors.items);
      errors.items.forEach((itemError, index) => {
        console.error(`Item ${index} errors:`, itemError);
      });
    }

    // Log current form values for debugging
    const currentValues = getValues();
    console.error("Current form values:", currentValues);
    if (currentValues.items) {
      console.error("Current items:", currentValues.items);
      console.error("Items length:", currentValues.items.length);
      if (currentValues.items.length > 0) {
        console.error("First item details:", currentValues.items[0]);
        console.error(
          "First item codeNumber:",
          currentValues.items[0].codeNumber,
        );
      }
    }

    toast({
      title: "Action failed",
      variant: "destructive",
      description,
      duration: 5000,
    });
  };

  const handleSelectItem = (item: z.infer<typeof invoiceItemSchema>) => {
    append(item);

    handleCalculation(fields.length, "quantity", 1);
  };

  const handleSelectBuyer = (buyer: z.infer<typeof buyerSchema>) => {
    setBuyerCode(buyer.code);
    setValue("buyerRegistrationType", buyer.registrationType);
    setValue("buyerRegistrationNumber", buyer.registrationNumber);
    setValue("buyerTin", buyer.tin);
    setValue("buyerName", buyer.name);
    setValue("buyerSstRegistrationNumber", buyer.sstRegistrationNumber);
    setValue("buyerEmail", buyer.email);
    setValue("buyerContact", buyer.contactNumber);
    setValue("buyerAddressLine0", buyer.buyerAddress.addressLine0);
    setValue("buyerAddressLine1", buyer.buyerAddress.addressLine1);
    setValue("buyerAddressLine2", buyer.buyerAddress.addressLine2);
    setValue("buyerCity", buyer.buyerAddress.cityName);
    setValue("buyerPostcode", buyer.buyerAddress.postalZone);
    setValue("buyerState", buyer.buyerAddress.state);
    setValue("buyerCountry", buyer.buyerAddress.country);
  };

  const handleSelectSupplier = (supplier: z.infer<typeof supplierSchema>) => {
    setSupplierCode(supplier.code);
    setValue("supplierRegistrationType", supplier.registrationType);
    setValue("supplierRegistrationNumber", supplier.registrationNumber);
    setValue("supplierTin", supplier.tin);
    setValue("supplierName", supplier.name);
    setValue("supplierSstRegistrationNumber", supplier.sstRegistrationNumber);
    setValue("supplierMsicCode", supplier.msicCode);
    setValue(
      "supplierTourismTaxRegistrationNumber",
      supplier.tourismTaxRegistrationNumber,
    );
    setValue(
      "supplierBusinessActivityDescription",
      supplier.businessActivityDescription,
    );
    setValue("supplierEmail", supplier.email);
    setValue("supplierContact", supplier.contactNumber);
    setValue("supplierAddressLine0", supplier.address.addressLine0);
    setValue("supplierAddressLine1", supplier.address.addressLine1);
    setValue("supplierAddressLine2", supplier.address.addressLine2);
    setValue("supplierCity", supplier.address.cityName);
    setValue("supplierPostcode", supplier.address.postalZone);
    setValue("supplierState", supplier.address.state);
    setValue("supplierCountry", supplier.address.country);
  };

  // Auto-fill buyer information from an existing invoice record
  const autofillBuyerFromInvoice = (invoice: any) => {
    setBuyerCode(invoice.buyerCode ?? null);
    setValue("buyerRegistrationType", invoice.buyerRegistrationType);
    setValue("buyerRegistrationNumber", invoice.buyerRegistrationNumber);
    setValue("buyerTin", invoice.buyerTin);
    setValue("buyerName", invoice.buyerName);
    setValue("buyerSstRegistrationNumber", invoice.buyerSstRegistrationNumber);
    setValue("buyerEmail", invoice.buyerEmail);
    setValue("buyerContact", invoice.buyerContact);
    setValue("buyerAddressLine0", invoice.buyerAddressLine0);
    setValue("buyerAddressLine1", invoice.buyerAddressLine1);
    setValue("buyerAddressLine2", invoice.buyerAddressLine2);
    setValue("buyerCity", invoice.buyerCity);
    setValue("buyerPostcode", invoice.buyerPostcode);
    setValue("buyerState", invoice.buyerState);
    setValue("buyerCountry", invoice.buyerCountry);
  };

  // Auto-fill supplier information from an existing invoice record
  const autofillSupplierFromInvoice = (invoice: any) => {
    setSupplierCode(invoice.supplierCode ?? null);
    setValue("supplierRegistrationType", invoice.supplierRegistrationType);
    setValue("supplierRegistrationNumber", invoice.supplierRegistrationNumber);
    setValue("supplierTin", invoice.supplierTin);
    setValue("supplierName", invoice.supplierName);
    setValue(
      "supplierSstRegistrationNumber",
      invoice.supplierSstRegistrationNumber,
    );
    setValue("supplierMsicCode", invoice.supplierMsicCode);
    setValue(
      "supplierTourismTaxRegistrationNumber",
      invoice.supplierTourismTaxRegistrationNumber,
    );
    setValue(
      "supplierBusinessActivityDescription",
      invoice.supplierBusinessActivityDescription,
    );
    setValue("supplierEmail", invoice.supplierEmail);
    setValue("supplierContact", invoice.supplierContact);
    setValue("supplierAddressLine0", invoice.supplierAddressLine0);
    setValue("supplierAddressLine1", invoice.supplierAddressLine1);
    setValue("supplierAddressLine2", invoice.supplierAddressLine2);
    setValue("supplierCity", invoice.supplierCity);
    setValue("supplierPostcode", invoice.supplierPostcode);
    setValue("supplierState", invoice.supplierState);
    setValue("supplierCountry", invoice.supplierCountry);
  };

  const handleConsolidateInvoice = (invoices: InvoiceList[]) => {
    // Validate all selected invoices share the same currency
    const currencies = [...new Set(invoices.map((inv) => inv.currencyCode))];
    if (currencies.length > 1) {
      toast({
        title: "Unable to Consolidate",
        description:
          "Unable to consolidate different foreign currency invoices together. Kindly do the consolidate e-invoice separately.",
        variant: "destructive",
      });
      return;
    }

    // Auto-set currency code from selected invoices
    if (currencies.length === 1) {
      setValue("currencyCode", currencies[0]);
    }

    const invoicesWithItemSchema: z.infer<typeof invoiceItemSchema>[] =
      invoices.flatMap((invoice) => {
        const raw = invoice.totalTaxAmountPerTaxType;
        const taxEntries: ConsolidateTaxEntry[] = Array.isArray(raw)
          ? raw
          : typeof raw === "string"
            ? (() => {
                try {
                  return JSON.parse(raw);
                } catch {
                  return [];
                }
              })()
            : [];
        // Fallback: if no per-type breakdown, treat entire tax amount as Sales Tax
        const effectiveTaxEntries =
          taxEntries.length > 0
            ? taxEntries
            : [{ taxType: "01", taxAmount: Number(invoice.totalTaxAmount) }];

        // Compute totalIncludingTax for this sub-invoice as excl + all tax entries
        const subInvoiceTotalTax = effectiveTaxEntries.reduce(
          (sum, entry) => sum + entry.taxAmount,
          0,
        );
        // Use totalPayableAmount directly
        const subInvoiceTotalPayable = Number(invoice.totalPayableAmount);
        // Derive excl-tax from payable minus the per-type tax sum
        const subInvoiceExclTax = subInvoiceTotalPayable - subInvoiceTotalTax;

        // Give each tax type the share of the net
        const netByEntry = allocateNetByTaxType(
          effectiveTaxEntries,
          subInvoiceExclTax,
        );

        return effectiveTaxEntries.map((taxEntry, index) => ({
          id: index === 0 ? invoice.id : undefined,
          classification: "004",
          description: invoice.codeNumber,
          unitPrice: netByEntry[index],
          taxType: taxEntry.taxType as
            | "01"
            | "02"
            | "03"
            | "04"
            | "05"
            | "06"
            | "E",
          codeNumber: invoice.codeNumber,
          taxRate: 0,
          taxAmount: taxEntry.taxAmount,
          detailsOfTaxExemption: null,
          amountExemptedFromTax: null,
          subtotal: round2(netByEntry[index] + taxEntry.taxAmount),
          totalExcludingTax: netByEntry[index],
          quantity: 1,
          measurement: null,
          discountRate: null,
          discountAmount: null,
          feeRate: null,
          feeAmount: null,
          productTariffCode: null,
          total: round2(netByEntry[index] + taxEntry.taxAmount),
          countryOfOrigin: getValues("supplierCountry"),
        }));
      });
    setValue("items", invoicesWithItemSchema);
  };

  const handleSelectEInvoice = async (EInvoices: EInvoice[]) => {
    const currentReferences = getValues("originalInvoiceReference") || [];
    const currentInvoiceIds = currentReferences.map(
      (ref: any) => ref.invoiceId,
    );

    const newInvoiceIds = EInvoices.map((e) => e.invoiceId);
    const deselectedInvoiceIds = currentInvoiceIds.filter(
      (id: string) => !newInvoiceIds.includes(id),
    );
    const newlySelectedInvoices = EInvoices.filter(
      (e) => !currentInvoiceIds.includes(e.invoiceId),
    );

    const reference = EInvoices.map((value) => ({
      id: value.internalId,
      uuid: value.id,
      invoiceId: value.invoiceId,
    }));
    const referenceNumber = EInvoices.map((value) => value.id).join(" ; ");
    setInvoiceReferenceSubstitution(referenceNumber);
    setValue("originalInvoiceReference", reference);

    if (deselectedInvoiceIds.length > 0) {
      const currentItems = getValues("items") || [];
      const remainingItems = currentItems.filter((item: any) => {
        return !deselectedInvoiceIds.includes(item.sourceInvoiceId);
      });
      setValue("items", remainingItems);
    }

    if (newlySelectedInvoices.length > 0) {
      try {
        const invoiceIds = newlySelectedInvoices.map((e) => e.invoiceId);
        const response = await api.post("/e-invoices/by-ids", { invoiceIds });
        const invoicesWithLineItems = response.data;

        // Auto-fill the buyer/supplier information
        const sourceInvoice = invoicesWithLineItems?.[0];
        if (sourceInvoice) {
          if (["12", "13", "14"].includes(documentType)) {
            autofillSupplierFromInvoice(sourceInvoice);
          } else {
            autofillBuyerFromInvoice(sourceInvoice);
          }
        }

        // Copy all line items from selected invoices
        const newLineItems: z.infer<typeof invoiceItemSchema>[] = [];

        for (const invoice of invoicesWithLineItems) {
          if (invoice.lineItems && Array.isArray(invoice.lineItems)) {
            for (const lineItem of invoice.lineItems) {
              const totalExcludingTax = Number(lineItem.totalExcludingTax) || 0;
              const taxAmount = Number(lineItem.taxAmount) || 0;
              const total = totalExcludingTax + taxAmount;

              newLineItems.push({
                classification: lineItem.classification,
                description: lineItem.description,
                unitPrice: Number(lineItem.unitPrice),
                taxType: lineItem.taxType,
                taxRate: Number(lineItem.taxRate),
                taxAmount: taxAmount,
                detailsOfTaxExemption: lineItem.detailsOfTaxExemption,
                amountExemptedFromTax: lineItem.amountExemptedFromTax,
                subtotal: Number(lineItem.subtotal),
                totalExcludingTax: totalExcludingTax,
                quantity: Number(lineItem.quantity),
                measurement: lineItem.measurement,
                discountRate: Number(lineItem.discountRate),
                discountAmount: Number(lineItem.discountAmount),
                feeRate: Number(lineItem.feeChargeRate ?? 0),
                feeAmount: Number(lineItem.feeChargeAmount ?? 0),
                productTariffCode: lineItem.productTariffCode,
                codeNumber: lineItem.itemCode, // Map itemCode to codeNumber for frontend schema
                countryOfOrigin: lineItem.countryOfOrigin,
                total: total,
                sourceInvoiceId: invoice.id,
              } as any);
            }
          }
        }

        append(newLineItems);
      } catch (error: any) {
        console.error("Error fetching invoice details:", error);
        toast({
          title: "Error",
          variant: "destructive",
          description:
            error?.response?.data?.message ||
            error?.message ||
            "Failed to fetch invoice details. Please try again.",
          icon: <CircleAlert size={30} className="animate-spin-once" />,
          duration: 4000,
        });
      }
    }
  };

  const handleRemoveItem = (index: number) => {
    remove(index);

    const currentItems = getValues("items") || [];
    if (currentItems.length === 1) {
      setValue("originalInvoiceReference", []);
      setInvoiceReferenceSubstitution(undefined);
    }
  };

  //manual insert line item
  const handleAddLineItem = () => {
    append(newLineItem);
    handleCalculation(items.length, "quantity", newLineItem.quantity);
    setNewLineItem(lineItemDefaultValue);
  };

  //manual insert original invoice reference uuid
  const handleInvoiceReference = (value: string) => {
    setInvoiceReferenceSubstitution((prev) => {
      const newValue = prev ? prev + " ; " + value : value;
      const uuids = newValue.split(" ; ");

      const references = uuids.map((value, index) => ({
        id: "ManualInsert" + index,
        uuid: value,
      }));
      setValue("originalInvoiceReference", references);
      return newValue;
    });
  };

  const handleSelectDeliveryOrder = async (deliveryOrderListItem: any) => {
    try {
      // Fetch full delivery order details including line items
      const deliveryOrderResponse = await deliveryOrdersApi.getById(
        deliveryOrderListItem.id,
      );
      const deliveryOrder = deliveryOrderResponse;

      if (!deliveryOrder || !deliveryOrder.id) {
        throw new Error("Invalid delivery order data received from API");
      }

      // Pre-fill buyer information
      setBuyerCode(deliveryOrder.buyerCode);
      setValue("buyerRegistrationType", deliveryOrder.buyerRegistrationType);
      setValue(
        "buyerRegistrationNumber",
        deliveryOrder.buyerRegistrationNumber,
      );
      setValue("buyerTin", deliveryOrder.buyerTin || "");
      setValue("buyerName", deliveryOrder.buyerName);
      setValue(
        "buyerSstRegistrationNumber",
        deliveryOrder.buyerSstRegistrationNumber || "",
      );
      setValue("buyerEmail", deliveryOrder.buyerEmail || "");
      setValue("buyerContact", deliveryOrder.buyerContact || "");
      setValue("buyerAddressLine0", deliveryOrder.buyerAddressLine0 || "");
      setValue("buyerAddressLine1", deliveryOrder.buyerAddressLine1 || "");
      setValue("buyerAddressLine2", deliveryOrder.buyerAddressLine2 || "");
      setValue("buyerCity", deliveryOrder.buyerCity || "");
      setValue("buyerPostcode", deliveryOrder.buyerPostcode || "");
      setValue("buyerState", deliveryOrder.buyerState || "");
      setValue("buyerCountry", deliveryOrder.buyerCountry || "");

      // Pre-fill document details
      setValue("currencyCode", deliveryOrder.currencyCode);
      setValue("currencyExchangeRate", deliveryOrder.currencyExchangeRate ?? 1);
      setValue("jobCode", deliveryOrder.jobCode || "");

      // Pre-fill line items - map delivery order items to invoice items
      const mappedItems = deliveryOrder.lineItems.map((item: any) => {
        const totalExcludingTax = Number(item.totalExcludingTax) || 0;
        const taxAmount = Number(item.taxAmount) || 0;
        const total = totalExcludingTax + taxAmount;

        return {
          classification: item.classification || "",
          description: item.description || "",
          unitPrice: Number(item.unitPrice) || 0,
          taxType: item.taxType || "",
          taxRate: Number(item.taxRate) || 0,
          taxAmount: taxAmount,
          detailsOfTaxExemption: item.detailsOfTaxExemption || null,
          amountExemptedFromTax: item.amountExemptedFromTax
            ? Number(item.amountExemptedFromTax)
            : null,
          subtotal: Number(item.subtotal) || 0,
          totalExcludingTax: totalExcludingTax,
          quantity: Number(item.quantity) || 0,
          measurement: item.measurement || null,
          discountRate: Number(item.discountRate) || 0,
          discountAmount: Number(item.discountAmount) || 0,
          feeRate: Number(item.feeChargeRate) || 0,
          feeAmount: Number(item.feeChargeAmount) || 0,
          productTariffCode: item.productTariffCode || null,
          codeNumber: item.itemCode || "DEFAULT_ITEM",
          countryOfOrigin: item.countryOfOrigin || null,
          total: total,
        } as any;
      });
      setValue("items", mappedItems);

      // Pre-fill additional charges
      setValue(
        "invoiceAdditionalDiscountAmount",
        deliveryOrder.additionalDiscountAmount || 0,
      );
      setValue(
        "invoiceAdditionalFeeAmount",
        deliveryOrder.additionalFeeAmount || 0,
      );
      setValue("roundingAmount", deliveryOrder.roundingAmount || 0);

      // Pre-fill shipping information from delivery order
      if (deliveryOrder.shipping) {
        setValue("shipping", deliveryOrder.shipping);
      }

      // Store delivery order reference
      setSelectedDeliveryOrderNumber(deliveryOrder.codeNumber);
      setSelectedDeliveryOrderId(deliveryOrder.id);

      toast({
        title: "Delivery Order Loaded",
        description: `All fields have been pre-filled from delivery order ${deliveryOrder.codeNumber}`,
        icon: (
          <CheckCircle size={30} className="animate-spin-once text-green-400" />
        ),
        duration: 5000,
      });
    } catch (error: any) {
      console.error("Error loading delivery order:", error);
      toast({
        title: "Error Loading Delivery Order",
        description:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to load delivery order details. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const handleSelectSalesOrder = async (salesOrderListItem: any) => {
    try {
      const salesOrder = await salesOrdersApi.getById(salesOrderListItem.id);

      if (!salesOrder || !salesOrder.id) {
        throw new Error("Invalid sales order data received from API");
      }

      // Pre-fill buyer information
      setBuyerCode(salesOrder.buyerCode);
      setValue("buyerRegistrationType", salesOrder.buyerRegistrationType);
      setValue("buyerRegistrationNumber", salesOrder.buyerRegistrationNumber);
      setValue("buyerTin", salesOrder.buyerTin || "");
      setValue("buyerName", salesOrder.buyerName);
      setValue(
        "buyerSstRegistrationNumber",
        salesOrder.buyerSstRegistrationNumber || "",
      );
      setValue("buyerEmail", salesOrder.buyerEmail || "");
      setValue("buyerContact", salesOrder.buyerContact || "");
      setValue("buyerAddressLine0", salesOrder.buyerAddressLine0 || "");
      setValue("buyerAddressLine1", salesOrder.buyerAddressLine1 || "");
      setValue("buyerAddressLine2", salesOrder.buyerAddressLine2 || "");
      setValue("buyerCity", salesOrder.buyerCity || "");
      setValue("buyerPostcode", salesOrder.buyerPostcode || "");
      setValue("buyerState", salesOrder.buyerState || "");
      setValue("buyerCountry", salesOrder.buyerCountry || "");

      // Pre-fill document details
      setValue("currencyCode", salesOrder.currencyCode);
      setValue("currencyExchangeRate", salesOrder.currencyExchangeRate ?? 1);
      if (salesOrder.jobCode) setValue("jobCode", salesOrder.jobCode);

      // Pre-fill line items
      const mappedItems = salesOrder.lineItems.map((item: any) => {
        const totalExcludingTax = Number(item.totalExcludingTax) || 0;
        const taxAmount = Number(item.taxAmount) || 0;
        const total = totalExcludingTax + taxAmount;

        return {
          classification: item.classification || "",
          description: item.description || "",
          unitPrice: Number(item.unitPrice) || 0,
          taxType: item.taxType || "",
          taxRate: Number(item.taxRate) || 0,
          taxAmount: taxAmount,
          detailsOfTaxExemption: item.detailsOfTaxExemption || null,
          amountExemptedFromTax: item.amountExemptedFromTax
            ? Number(item.amountExemptedFromTax)
            : null,
          subtotal: Number(item.subtotal) || 0,
          totalExcludingTax: totalExcludingTax,
          quantity: Number(item.quantity) || 0,
          measurement: item.measurement || null,
          discountRate: Number(item.discountRate) || 0,
          discountAmount: Number(item.discountAmount) || 0,
          feeRate: Number(item.feeChargeRate) || 0,
          feeAmount: Number(item.feeChargeAmount) || 0,
          productTariffCode: item.productTariffCode || null,
          codeNumber: item.itemCode || "DEFAULT_ITEM",
          countryOfOrigin: item.countryOfOrigin || null,
          total: total,
        } as any;
      });
      setValue("items", mappedItems);

      // Pre-fill additional charges
      setValue(
        "invoiceAdditionalDiscountAmount",
        salesOrder.additionalDiscountAmount || 0,
      );
      setValue(
        "invoiceAdditionalFeeAmount",
        salesOrder.additionalFeeAmount || 0,
      );
      setValue("roundingAmount", salesOrder.roundingAmount || 0);

      // Pre-fill shipping information if present
      if (salesOrder.shipping) {
        setValue("shipping", salesOrder.shipping);
      }

      setSelectedSalesOrderNumber(salesOrder.codeNumber);

      toast({
        title: "Sales Order Loaded",
        description: `All fields have been pre-filled from sales order ${salesOrder.codeNumber}`,
        icon: (
          <CheckCircle size={30} className="animate-spin-once text-green-400" />
        ),
        duration: 5000,
      });
    } catch (error: any) {
      console.error("Error loading sales order:", error);
      toast({
        title: "Error Loading Sales Order",
        description:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to load sales order details. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const getLocalDateTimeString = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset();
    const localDate = new Date(now.getTime() - offset * 60 * 1000);

    return localDate.toISOString().slice(0, 16);
  };

  return (
    <Card className="w-full max-w-6xl mx-auto shadow p-2 pt-6">
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit, onError)} className="space-y-6">
          <div className="flex flex-col md:flex-row justify-between items-center md:items-start gap-6 md:gap-0">
            <div className="static md:absolute flex flex-row">
              <h1 className="text-xl font-bold text-center md:text-left">
                {DocumentTypeLabels[documentType] || "Unknown Type"}
              </h1>
            </div>
            {/* Company Details - Middle */}
            <div className="flex flex-col items-center justify-center w-full text-center space-y-3">
              <h1 className="text-2xl font-bold text-center md:text-left">
                {user.businessName}
              </h1>
              {currentActiveBranch &&
                currentActiveBranch.prefixCode !== "HQ" && (
                  <p className="text-base font-medium text-gray-700">
                    {currentActiveBranch.branchName}
                  </p>
                )}

              <div className="flex items-center space-x-2 text-gray-600">
                <Phone size={16} className="flex-shrink-0" />
                <span className="text-sm">
                  {(currentActiveBranch?.prefixCode !== "HQ" &&
                    currentActiveBranch?.contactNumber) ||
                    user.contactNumber}
                </span>
              </div>

              <div className="flex items-center space-x-2 text-gray-600">
                <Mail size={16} className="flex-shrink-0" />
                <span className="text-sm">
                  {(currentActiveBranch?.prefixCode !== "HQ" &&
                    currentActiveBranch?.email) ||
                    user.email}
                </span>
              </div>
              <div className="flex items-center space-x-2 text-gray-600">
                <Building2 size={16} className="flex-shrink-0" />
                <span className="text-sm">
                  {currentActiveBranch &&
                  currentActiveBranch.prefixCode !== "HQ"
                    ? [
                        currentActiveBranch.addressLine0,
                        currentActiveBranch.addressLine1,
                        currentActiveBranch.addressLine2,
                        currentActiveBranch.cityName,
                      ]
                        .filter(Boolean)
                        .join(", ")
                    : [
                        user.addressLine0,
                        user.addressLine1,
                        user.addressLine2,
                        user.cityName,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                </span>
              </div>
            </div>
          </div>

          <Tabs.Root defaultValue="buyerOrSupplier">
            <Tabs.List color="gray" justify="start">
              <Tabs.Trigger value="buyerOrSupplier">
                {["11", "12", "13", "14"].includes(documentType)
                  ? "Supplier Information"
                  : "Buyer Information"}
                <span className="text-red-600">*</span>
              </Tabs.Trigger>
              <Tabs.Trigger value="invoiceDetail">
                Invoice Detail<span className="text-red-600">*</span>
              </Tabs.Trigger>
              <Tabs.Trigger value="frequentOfBilling">
                Frequent Of Billing
              </Tabs.Trigger>
              <Tabs.Trigger value="paymentAndPrepayment">
                Payment & Prepayment
              </Tabs.Trigger>
              <Tabs.Trigger value="shipping">Shipping</Tabs.Trigger>
              <Tabs.Trigger value="importAndExport">
                Import & Export
              </Tabs.Trigger>
            </Tabs.List>

            <Box pt="3">
              <Tabs.Content value="buyerOrSupplier">
                {/* Supplier Details Section */}
                {["11", "12", "13", "14"].includes(documentType) && (
                  <div className="space-y-4">
                    <div className="flex flex-row gap-2 items-center">
                      <h3 className="text-lg font-semibold">
                        Supplier Information
                      </h3>
                      <Button
                        onClick={() => setIsSearchSupplierModalOpen(true)}
                        variant="outline"
                        size="sm"
                        type="button"
                      >
                        Find
                        <Search />
                      </Button>
                      <SearchSupplierModal
                        isOpen={isSearchSupplierModalOpen}
                        onClose={() => setIsSearchSupplierModalOpen(false)}
                        onSelectSupplier={handleSelectSupplier}
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="supplierRegistrationType" required>
                          ID Type
                        </Label>
                        <Controller
                          name="supplierRegistrationType"
                          control={control}
                          render={({ field }) => (
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <SelectTrigger className="h-10">
                                <SelectValue placeholder="-- Select type --" />
                              </SelectTrigger>
                              <SelectContent>
                                {registrationTypes.map((type) => (
                                  <SelectItem
                                    key={type.value}
                                    value={type.value}
                                  >
                                    {type.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                        {errors.supplierRegistrationType && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.supplierRegistrationType.message?.toString()}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="supplierRegistrationNumber" required>
                          Registration / Identification / Passport Number
                        </Label>
                        <Input {...register("supplierRegistrationNumber")} />
                        {errors.supplierRegistrationNumber && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.supplierRegistrationNumber.message?.toString()}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="supplierTin" required>
                          TIN
                        </Label>
                        <div className="flex gap-1">
                          <Input
                            {...register("supplierTin")}
                            disabled={documentType === "01A"}
                          />
                          <Button
                            variant="outline"
                            type="button"
                            onClick={() => setOpenTinSearch(true)}
                            className="group"
                          >
                            <Settings />
                          </Button>
                        </div>
                        {errors.supplierTin && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.supplierTin.message?.toString()}
                          </p>
                        )}
                      </div>
                      <TinSearch
                        isOpen={openTinSearch}
                        onClose={() => setOpenTinSearch(false)}
                        onSelect={(value) => setValue("supplierTin", value)}
                      />
                      {["12", "13", "14"].includes(documentType) && (
                        <div>
                          <Label htmlFor="originalInvoiceReference" required>
                            Original e-Invoice Reference Number (UUID)
                          </Label>
                          <div className="flex gap-1">
                            <Input
                              value={invoiceReferenceSubstitution}
                              disabled
                            />
                            <Button
                              variant="outline"
                              type="button"
                              onClick={() => setOpenEInvoice(true)}
                            >
                              <Search />
                            </Button>
                            <Button
                              variant="outline"
                              type="button"
                              onClick={() => setOpenInsertReference(true)}
                            >
                              <Plus />
                            </Button>
                          </div>
                          {errors?.originalInvoiceReference && (
                            <p className="text-red-600 text-sm mt-1">
                              {errors.originalInvoiceReference.message?.toString()}
                            </p>
                          )}
                          <SearchInvoiceReferenceNumber
                            isOpen={openEInvoice}
                            onClose={() => setOpenEInvoice(false)}
                            onSelect={(e) => handleSelectEInvoice(e)}
                            buyerTin={supplierTin}
                            defaultChecked={(
                              getValues("originalInvoiceReference") || []
                            ).map((ref: any) => ref.invoiceId)}
                          />
                          <InsertEInvoiceReference
                            isOpen={openInsertReference}
                            onClose={() => setOpenInsertReference(false)}
                            onFilled={(uuid) => handleInvoiceReference(uuid)}
                          />
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="supplierName" required>
                          Name
                        </Label>
                        <Input {...register("supplierName")} />
                        {errors.supplierName && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.supplierName.message?.toString()}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="supplierSstRegistrationNumber">
                          SST Registration Number
                        </Label>
                        <Input {...register("supplierSstRegistrationNumber")} />
                        {errors.supplierSstRegistrationNumber && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.supplierSstRegistrationNumber.message?.toString()}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="supplierMsicCode" required>
                          MSIC Code
                        </Label>
                        <Input {...register("supplierMsicCode")} />
                        {errors.supplierMsicCode && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.supplierMsicCode.message?.toString()}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label
                          htmlFor="supplierBusinessActivityDescription"
                          required
                        >
                          Business Activity Description
                        </Label>
                        <Input
                          {...register("supplierBusinessActivityDescription")}
                        />
                        {errors.supplierBusinessActivityDescription && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.supplierBusinessActivityDescription.message?.toString()}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="supplierTourismTaxRegistrationNumber">
                          Tourism Tax Registration Number
                        </Label>
                        <Input
                          {...register("supplierTourismTaxRegistrationNumber")}
                        />
                        {errors.supplierTourismTaxRegistrationNumber && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.supplierTourismTaxRegistrationNumber.message?.toString()}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="supplierEmail" required>
                          Email
                        </Label>
                        <Input {...register("supplierEmail")} type="email" />
                        {errors.supplierEmail && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.supplierEmail.message?.toString()}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="supplierContact" required>
                          Contact Number
                        </Label>
                        <Input {...register("supplierContact")} />
                        {errors.supplierContact && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.supplierContact.message?.toString()}
                          </p>
                        )}
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="supplierAddressLine0" required>
                        Address Line 1
                      </Label>
                      <Input {...register("supplierAddressLine0")} />
                      <AddressCharCount value={watch("supplierAddressLine0")} />
                      {errors.supplierAddressLine0 && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.supplierAddressLine0.message?.toString()}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="supplierAddressLine1">
                        Address Line 2
                      </Label>
                      <Input {...register("supplierAddressLine1")} />
                      <AddressCharCount value={watch("supplierAddressLine1")} />
                      {errors.supplierAddressLine1 && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.supplierAddressLine1.message?.toString()}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="supplierAddressLine2">
                        Address Line 3
                      </Label>
                      <Input {...register("supplierAddressLine2")} />
                      <AddressCharCount value={watch("supplierAddressLine2")} />
                      {errors.supplierAddressLine2 && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.supplierAddressLine2.message?.toString()}
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="supplierCity" required>
                          City
                        </Label>
                        <Input {...register("supplierCity")} />
                        {errors.supplierCity && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.supplierCity.message?.toString()}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="supplierPostcode" required>
                          Postcode
                        </Label>
                        <Input {...register("supplierPostcode")} />
                        {errors.supplierPostcode && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.supplierPostcode.message?.toString()}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="supplierCountry" required>
                          Country
                        </Label>
                        <Controller
                          name="supplierCountry"
                          control={control}
                          render={({ field }) => (
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <SelectTrigger className="h-10">
                                <SelectValue placeholder="-- Select Country --" />
                              </SelectTrigger>
                              <SelectContent>
                                {countries.map((country) => (
                                  <SelectItem
                                    key={country.Code}
                                    value={country.Code}
                                  >
                                    {country.Country}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                        {errors.supplierCountry && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.supplierCountry.message?.toString()}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="supplierState" required>
                          State
                        </Label>
                        <Controller
                          name="supplierState"
                          control={control}
                          render={({ field }) => (
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <SelectTrigger className="h-10">
                                <SelectValue placeholder="-- Select State --" />
                              </SelectTrigger>
                              <SelectContent>
                                {states.map((state) => (
                                  <SelectItem
                                    key={state.Code}
                                    value={state.Code}
                                  >
                                    {state.State}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                        {errors.supplierState && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.supplierState.message?.toString()}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Buyer Details Section */}
                {["01", "02", "03", "04", "01A"].includes(documentType) && (
                  <>
                    {documentType !== "01A" && (
                      <SearchBuyerModal
                        isOpen={isSearchBuyerModalOpen}
                        onClose={() => setIsSearchBuyerModalOpen(false)}
                        onSelectBuyer={handleSelectBuyer}
                      />
                    )}

                    <div className="space-y-4">
                      <div
                        className={`${
                          documentType !== "01A"
                            ? "flex flex-row gap-2 items-center"
                            : "hidden"
                        }`}
                      >
                        <h3 className="text-lg font-semibold">
                          Buyer Information{" "}
                        </h3>
                        <Button
                          onClick={() => setIsSearchBuyerModalOpen(true)}
                          type="button"
                          variant="outline"
                          size="sm"
                        >
                          Find
                          <Search />
                        </Button>
                      </div>
                      {buyerCode && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span className="font-medium text-foreground">
                            Customer Code:
                          </span>
                          <span className="font-mono bg-muted px-2 py-0.5 rounded text-foreground">
                            {buyerCode}
                          </span>
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="buyerRegistrationType" required>
                            ID Type
                          </Label>
                          <Controller
                            name="buyerRegistrationType"
                            control={control}
                            render={({ field }) => (
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                                disabled={documentType === "01A"}
                              >
                                <SelectTrigger className="h-10">
                                  <SelectValue
                                    placeholder={`${
                                      documentType !== "01A"
                                        ? "-- Select type --"
                                        : "NA"
                                    }`}
                                  />
                                </SelectTrigger>
                                <SelectContent>
                                  {registrationTypes.map((type) => (
                                    <SelectItem
                                      key={type.value}
                                      value={type.value}
                                    >
                                      {type.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          />
                          {errors.buyerRegistrationType && (
                            <p className="text-red-600 text-sm mt-1">
                              {errors.buyerRegistrationType.message?.toString()}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="buyerRegistrationNumber" required>
                            Registration / Identification / Passport Number
                          </Label>
                          <Input
                            {...register("buyerRegistrationNumber")}
                            disabled={documentType === "01A"}
                          />
                          {errors.buyerRegistrationNumber && (
                            <p className="text-red-600 text-sm mt-1">
                              {errors.buyerRegistrationNumber.message?.toString()}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="buyerTin" required>
                            TIN
                          </Label>
                          <div className="flex gap-1">
                            <Input
                              {...register("buyerTin")}
                              disabled={documentType === "01A"}
                            />
                            <Button
                              variant="outline"
                              type="button"
                              disabled={documentType === "01A"}
                              onClick={() => setOpenTinSearch(true)}
                              className="group"
                            >
                              <Settings />
                            </Button>
                          </div>
                          {errors.buyerTin && (
                            <p className="text-red-600 text-sm mt-1">
                              {errors.buyerTin.message?.toString()}
                            </p>
                          )}
                        </div>
                        <TinSearch
                          isOpen={openTinSearch}
                          onClose={() => setOpenTinSearch(false)}
                          onSelect={(value) => setValue("buyerTin", value)}
                        />
                        {["02", "03", "04"].includes(documentType) && (
                          <div>
                            <Label htmlFor="originalInvoiceReference" required>
                              Original e-Invoice Reference Number (UUID)
                            </Label>
                            <div className="flex gap-1">
                              <Input
                                value={invoiceReferenceSubstitution}
                                disabled
                              />
                              <Button
                                variant="outline"
                                type="button"
                                onClick={() => setOpenEInvoice(true)}
                              >
                                <Search />
                              </Button>
                              <Button
                                variant="outline"
                                type="button"
                                onClick={() => setOpenInsertReference(true)}
                              >
                                <Plus />
                              </Button>
                            </div>
                            {errors?.originalInvoiceReference && (
                              <p className="text-red-600 text-sm mt-1">
                                {errors.originalInvoiceReference.message?.toString()}
                              </p>
                            )}
                            <SearchInvoiceReferenceNumber
                              isOpen={openEInvoice}
                              onClose={() => setOpenEInvoice(false)}
                              onSelect={(e) => handleSelectEInvoice(e)}
                              buyerTin={buyerTin}
                              defaultChecked={(
                                getValues("originalInvoiceReference") || []
                              ).map((ref: any) => ref.invoiceId)}
                            />
                            <InsertEInvoiceReference
                              isOpen={openInsertReference}
                              onClose={() => setOpenInsertReference(false)}
                              onFilled={(uuid) => handleInvoiceReference(uuid)}
                            />
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="buyerName" required>
                            Name
                          </Label>
                          <Input
                            {...register("buyerName")}
                            disabled={documentType === "01A"}
                          />
                          {errors.buyerName && (
                            <p className="text-red-600 text-sm mt-1">
                              {errors.buyerName.message?.toString()}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="buyerSstRegistrationNumber">
                            SST Registration Number
                          </Label>
                          <Input
                            {...register("buyerSstRegistrationNumber")}
                            disabled={documentType === "01A"}
                          />
                          {errors.buyerSstRegistrationNumber && (
                            <p className="text-red-600 text-sm mt-1">
                              {errors.buyerSstRegistrationNumber.message?.toString()}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="buyerEmail" required>
                            Email
                          </Label>
                          <Input
                            {...register("buyerEmail")}
                            disabled={documentType === "01A"}
                          />
                          {errors.buyerEmail && (
                            <p className="text-red-600 text-sm mt-1">
                              {errors.buyerEmail.message?.toString()}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="buyerContact" required>
                            Contact Number
                          </Label>
                          <Input
                            {...register("buyerContact")}
                            disabled={documentType === "01A"}
                          />
                          {errors.buyerContact && (
                            <p className="text-red-600 text-sm mt-1">
                              {errors.buyerContact.message?.toString()}
                            </p>
                          )}
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="buyerAddressLine0" required>
                          Address Line 1
                        </Label>
                        <Input
                          {...register("buyerAddressLine0")}
                          disabled={documentType === "01A"}
                        />
                        <AddressCharCount value={watch("buyerAddressLine0")} />
                        {errors.buyerAddressLine0 && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.buyerAddressLine0.message?.toString()}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="buyerAddressLine1">
                          Address Line 2
                        </Label>
                        <Input
                          {...register("buyerAddressLine1")}
                          disabled={documentType === "01A"}
                        />
                        <AddressCharCount value={watch("buyerAddressLine1")} />
                        {errors.buyerAddressLine1 && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.buyerAddressLine1.message?.toString()}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="buyerAddressLine2">
                          Address Line 3
                        </Label>
                        <Input
                          {...register("buyerAddressLine2")}
                          disabled={documentType === "01A"}
                        />
                        <AddressCharCount value={watch("buyerAddressLine2")} />
                        {errors.buyerAddressLine2 && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.buyerAddressLine2.message?.toString()}
                          </p>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="buyerCity" required>
                            City
                          </Label>
                          <Input
                            {...register("buyerCity")}
                            disabled={documentType === "01A"}
                          />
                          {errors.buyerCity && (
                            <p className="text-red-600 text-sm mt-1">
                              {errors.buyerCity.message?.toString()}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="buyerPostcode" required>
                            Postcode
                          </Label>
                          <Input
                            {...register("buyerPostcode")}
                            disabled={documentType === "01A"}
                          />
                          {errors.buyerPostcode && (
                            <p className="text-red-600 text-sm mt-1">
                              {errors.buyerPostcode.message?.toString()}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="buyerCountry" required>
                            Country
                          </Label>
                          <Controller
                            name="buyerCountry"
                            control={control}
                            render={({ field }) => (
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                                disabled={documentType === "01A"}
                              >
                                <SelectTrigger className="h-10">
                                  <SelectValue placeholder="-- Select Country --" />
                                </SelectTrigger>
                                <SelectContent>
                                  {countries.map((country) => (
                                    <SelectItem
                                      key={country.Code}
                                      value={country.Code}
                                    >
                                      {country.Country}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          />
                          {errors.buyerCountry && (
                            <p className="text-red-600 text-sm mt-1">
                              {errors.buyerCountry.message?.toString()}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="buyerState" required>
                            State
                          </Label>
                          <Controller
                            name="buyerState"
                            control={control}
                            render={({ field }) => (
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                                disabled={documentType === "01A"}
                              >
                                <SelectTrigger className="h-10">
                                  <SelectValue placeholder="-- Select State --" />
                                </SelectTrigger>
                                <SelectContent>
                                  {states.map((state) => (
                                    <SelectItem
                                      key={state.Code}
                                      value={state.Code}
                                    >
                                      {state.State}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          />
                          {errors.buyerState && (
                            <p className="text-red-600 text-sm mt-1">
                              {errors.buyerState.message?.toString()}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </Tabs.Content>

              <Tabs.Content value="invoiceDetail">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Invoice Detail</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="issuanceDate">Issuance Datetime</Label>
                      <Input
                        id="issuanceDate"
                        type="datetime-local"
                        defaultValue={getLocalDateTimeString()}
                        disabled
                      />
                    </div>
                    <div>
                      <Label htmlFor="codeNumber" required>
                        Invoice Number
                      </Label>
                      <Controller
                        name="codeNumber"
                        control={control}
                        render={({ field }) => (
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <SelectTrigger>
                              <SelectValue
                                placeholder={
                                  codeNumberOptions?.find(
                                    (option) => option.isDefault === true,
                                  )?.codeNumber ||
                                  "No available prefix number added"
                                }
                              />
                            </SelectTrigger>
                            <SelectContent className="max-h-48">
                              {codeNumberOptions?.map((option) => (
                                <SelectItem
                                  key={option.id}
                                  value={option.codeNumber}
                                >
                                  {option.codeNumber}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                      {errors.codeNumber && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.codeNumber.message?.toString()}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="docDate">Invoice Date</Label>
                      <Input type="date" {...register("docDate")} />
                      {errors.docDate && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.docDate.message?.toString()}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="jobCode">Job Code</Label>
                      <Controller
                        name="jobCode"
                        control={control}
                        render={({ field }) => (
                          <Select
                            onValueChange={field.onChange}
                            value={field.value || undefined}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue
                                placeholder={
                                  jobCode
                                    ? "-- Select job code --"
                                    : "No job code available"
                                }
                              />
                            </SelectTrigger>
                            <SelectContent className="max-h-48 max-w-full">
                              {jobCode &&
                                Array.isArray(jobCode) &&
                                jobCode.map((item) => (
                                  <SelectItem key={item.code} value={item.code}>
                                    {item.code}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                      {errors.jobCode && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.jobCode.message?.toString()}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="currencyCode">
                        Invoice Currency Code
                      </Label>
                      <Controller
                        name="currencyCode"
                        control={control}
                        render={({ field }) => (
                          <Select
                            onValueChange={field.onChange}
                            value={field.value || undefined}
                            disabled={documentType === "01A"}
                            onOpenChange={(open) => {
                              if (!open) setCurrencySearch("");
                            }}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue placeholder="-- Select type --" />
                            </SelectTrigger>
                            <SelectContent>
                              <div className="px-2 py-1.5 sticky top-0 bg-popover z-10">
                                <Input
                                  placeholder="Search currency..."
                                  value={currencySearch}
                                  onChange={(e) =>
                                    setCurrencySearch(e.target.value)
                                  }
                                  onKeyDown={(e) => e.stopPropagation()}
                                  className="h-8 text-sm"
                                />
                              </div>
                              {(() => {
                                const q = currencySearch.toLowerCase();
                                const filtered = currencyCodes.filter(
                                  (c) =>
                                    c.Code.toLowerCase().includes(q) ||
                                    c.Currency.toLowerCase().includes(q) ||
                                    c.Symbol.toLowerCase().includes(q),
                                );
                                const myr = filtered.find(
                                  (c) => c.Code === "MYR",
                                );
                                const rest = filtered.filter(
                                  (c) => c.Code !== "MYR",
                                );
                                return [...(myr ? [myr] : []), ...rest].map(
                                  (currency) => (
                                    <SelectItem
                                      key={currency.Code}
                                      value={currency.Code}
                                    >
                                      {currency.Code} — {currency.Currency}
                                    </SelectItem>
                                  ),
                                );
                              })()}
                            </SelectContent>
                          </Select>
                        )}
                      />
                      {errors.currencyCode && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.currencyCode.message?.toString()}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="currencyExchangeRate">
                        Currency Exchange Rate
                      </Label>
                      <Input
                        type="number"
                        step="0.0001"
                        disabled={documentType === "01A"}
                        {...register("currencyExchangeRate", {
                          valueAsNumber: true,
                        })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="originalInvoiceNumber">
                        POS Reference Number (If applicable)
                      </Label>
                      <Input {...register("originalInvoiceNumber")} />
                      {errors.originalInvoiceNumber && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.originalInvoiceNumber.message?.toString()}
                        </p>
                      )}
                    </div>
                    {/* Only show SO/DO pickers for regular invoices (document type 01) */}
                    {documentType === DocumentType.INVOICE && (
                      <div>
                        <Label htmlFor="salesOrderPicker">
                          Pick from Sales Order
                        </Label>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setIsSalesOrderPickerOpen(true)}
                            className="flex items-center gap-2"
                          >
                            <Search size={16} />
                            Select Sales Order
                          </Button>
                          {selectedSalesOrderNumber && (
                            <div className="flex items-center text-sm text-gray-600">
                              Selected: {selectedSalesOrderNumber}
                            </div>
                          )}
                        </div>
                        <SalesOrderPickerModal
                          isOpen={isSalesOrderPickerOpen}
                          onClose={() => setIsSalesOrderPickerOpen(false)}
                          onSelectSalesOrder={handleSelectSalesOrder}
                          description="Search and select a sales order to pre-fill the invoice. Only showing orders that are not yet fulfilled."
                        />
                      </div>
                    )}
                    {documentType === DocumentType.INVOICE && (
                      <div>
                        <Label htmlFor="deliveryOrderPicker">
                          Pick from Delivery Order
                        </Label>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setIsDeliveryOrderPickerOpen(true)}
                            className="flex items-center gap-2"
                          >
                            <Search size={16} />
                            Select Delivery Order
                          </Button>
                          {selectedDeliveryOrderNumber && (
                            <div className="flex items-center text-sm text-gray-600">
                              Selected: {selectedDeliveryOrderNumber}
                            </div>
                          )}
                        </div>
                        <DeliveryOrderPickerModal
                          isOpen={isDeliveryOrderPickerOpen}
                          onClose={() => setIsDeliveryOrderPickerOpen(false)}
                          onSelectDeliveryOrder={handleSelectDeliveryOrder}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </Tabs.Content>

              {/* Frequent of Billing Information (all optional) */}
              <Tabs.Content value="frequentOfBilling">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">
                    Frequent of Billing Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="frequencyOfBilling">
                        Frequency Of Billing
                      </Label>
                      <Controller
                        name="frequencyOfBilling"
                        control={control}
                        render={({ field }) => (
                          <Select
                            value={field.value}
                            onValueChange={field.onChange}
                            disabled={documentType === "01A"}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue placeholder="-- Select type --" />
                            </SelectTrigger>
                            <SelectContent>
                              {frequencyOfBilling.map((frequency) => (
                                <SelectItem
                                  key={frequency.value}
                                  value={frequency.value}
                                >
                                  {frequency.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                      {errors.frequencyOfBilling && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.frequencyOfBilling.message?.toString()}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="billingPeriodStartDate">
                        Billing Period Start Date
                      </Label>
                      <Input
                        type="date"
                        {...register("billingPeriodStartDate")}
                        disabled={documentType === "01A"}
                      />
                      {errors.billingPeriodStartDate && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.billingPeriodStartDate.message?.toString()}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="billingPeriodEndDate">
                        Billing Period End Date
                      </Label>
                      <Input
                        type="date"
                        {...register("billingPeriodEndDate")}
                        disabled={documentType === "01A"}
                      />
                      {errors.billingPeriodEndDate && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.billingPeriodEndDate.message?.toString()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </Tabs.Content>

              {/* Payment and Prepayment Information (all optional) */}
              <Tabs.Content value="paymentAndPrepayment">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">
                    Payment and Prepayment Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="paymentMode">Payment Mode</Label>
                      <Controller
                        name="paymentMode"
                        control={control}
                        render={({ field }) => (
                          <Select
                            value={field.value}
                            onValueChange={field.onChange}
                            disabled={documentType === "01A"}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue placeholder="-- Select type --" />
                            </SelectTrigger>
                            <SelectContent>
                              {paymentMode.map((payment) => (
                                <SelectItem
                                  key={payment.Code}
                                  value={payment.Code}
                                >
                                  {payment["Payment Method"]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                      {errors.paymentMode && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.paymentMode.message?.toString()}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="supplierBankAccountNumber">
                        Supplier's Bank Account Number
                      </Label>
                      <Input
                        disabled={documentType === "01A"}
                        {...register("supplierBankAccountNumber")}
                      />
                      {errors.supplierBankAccountNumber && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.supplierBankAccountNumber.message?.toString()}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="paymentTerms">Payment Terms</Label>
                      <Input
                        disabled={documentType === "01A"}
                        {...register("paymentTerms")}
                      />
                      {errors.paymentTerms && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.paymentTerms.message?.toString()}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="prepaymentAmount">
                        Prepayment Amount (RM)
                      </Label>
                      <Input
                        disabled={documentType === "01A"}
                        {...register("prepaymentAmount", {
                          valueAsNumber: true,
                        })}
                      />
                      {errors.prepaymentAmount && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.prepaymentAmount.message?.toString()}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="prepaymentDate">Prepayment Date</Label>
                      <Input
                        disabled={documentType === "01A"}
                        type="date"
                        {...register("prepaymentDate")}
                      />
                      {errors.prepaymentDate && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.prepaymentDate.message?.toString()}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="prepaymentReferenceNumber">
                        Prepayment Reference Number
                      </Label>
                      <Input
                        disabled={documentType === "01A"}
                        {...register("prepaymentReferenceNumber")}
                      />
                      {errors.prepaymentReferenceNumber && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.prepaymentReferenceNumber.message?.toString()}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="billReferenceNumber">
                        Bill Reference Number
                      </Label>
                      <Input
                        disabled={documentType === "01A"}
                        {...register("billReferenceNumber")}
                      />
                      {errors.billReferenceNumber && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.billReferenceNumber.message?.toString()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </Tabs.Content>

              {/* Shipping Information (all optional) */}
              {/* Applicable to transactions where goods are shipped to a different recipient and/or address (i.e., different from the Buyer's details) */}
              <Tabs.Content value="shipping">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold">
                      Shipping Information
                    </h3>
                    <h5 className="text-sm font-light">
                      Applicable to transactions where goods are shipped to a
                      different recipient and/or address (i.e., different from
                      the Buyer's details)
                    </h5>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="shipping.recipientName">
                        Shipping Recipient's Name
                      </Label>
                      <Input
                        disabled={documentType === "01A"}
                        {...register("shipping.recipientName")}
                      />
                      {/* {errors.shipping?.recipientName && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.shipping?.recipientName.message?.toString()}
                        </p>
                      )} */}
                    </div>
                    <div>
                      <Label htmlFor="shipping.recipientAddressLine0">
                        Address Line 1
                      </Label>
                      <Input
                        disabled={documentType === "01A"}
                        {...register("shipping.recipientAddressLine0")}
                      />
                      {/* {errors.shippingAddressLine1 && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.shippingAddressLine1.message?.toString()}
                        </p>
                      )} */}
                    </div>
                    <div>
                      <Label htmlFor="shipping.recipientAddressLine1">
                        Address Line 2
                      </Label>
                      <Input
                        disabled={documentType === "01A"}
                        {...register("shipping.recipientAddressLine1")}
                      />
                    </div>
                    <div>
                      <Label htmlFor="shipping.recipientAddressLine2">
                        Address Line 3
                      </Label>
                      <Input
                        disabled={documentType === "01A"}
                        {...register("shipping.recipientAddressLine2")}
                      />
                    </div>
                    <div>
                      <Label htmlFor="shipping.recipientPostcode">
                        Postcode
                      </Label>
                      <Input
                        disabled={documentType === "01A"}
                        {...register("shipping.recipientPostcode")}
                      />
                    </div>
                    <div>
                      <Label htmlFor="shipping.recipientCity">City</Label>
                      <Input
                        disabled={documentType === "01A"}
                        {...register("shipping.recipientCity")}
                      />
                    </div>
                    <div>
                      <Label htmlFor="shipping.recipientCountry">Country</Label>
                      <Controller
                        name="shipping.recipientCountry"
                        control={control}
                        render={({ field }) => (
                          <Select
                            value={field.value}
                            onValueChange={field.onChange}
                            disabled={documentType === "01A"}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue placeholder="-- Select type --" />
                            </SelectTrigger>
                            <SelectContent>
                              {countries.map((country) => (
                                <SelectItem
                                  key={country.Code}
                                  value={country.Code}
                                >
                                  {country.Country}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                    <div>
                      <Label htmlFor="shipping.recipientState">State</Label>
                      <Controller
                        name="shipping.recipientState"
                        control={control}
                        render={({ field }) => (
                          <Select
                            value={field.value}
                            onValueChange={field.onChange}
                            disabled={documentType === "01A"}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue placeholder="-- Select type --" />
                            </SelectTrigger>
                            <SelectContent>
                              {states.map((state) => (
                                <SelectItem key={state.Code} value={state.Code}>
                                  {state.State}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                    <div>
                      <Label htmlFor="shipping.recipientRegistrationType">
                        ID Type
                      </Label>
                      <Controller
                        name="shipping.recipientRegistrationType"
                        control={control}
                        render={({ field }) => (
                          <Select
                            value={field.value}
                            onValueChange={field.onChange}
                            disabled={documentType === "01A"}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue placeholder="-- Select type --" />
                            </SelectTrigger>
                            <SelectContent>
                              {registrationTypes.map((type) => (
                                <SelectItem key={type.value} value={type.value}>
                                  {type.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                    <div>
                      <Label htmlFor="shipping.recipientRegistrationNumber">
                        Registration / Identification / Passport Number
                      </Label>
                      <Input
                        disabled={documentType === "01A"}
                        {...register("shipping.recipientRegistrationNumber")}
                      />
                    </div>
                    <div>
                      <Label htmlFor="shipping.recipientTin">
                        Shipping Recipient's TIN
                      </Label>
                      <Input
                        disabled={documentType === "01A"}
                        {...register("shipping.recipientTin")}
                      />
                    </div>
                  </div>
                </div>
              </Tabs.Content>

              {/* Import And Export Information (optional except Reference Number of Customs Form No.1,9 etc. is mandatory where applicable) */}
              {/* Applicable to transactions where goods are shipped to a different recipient and/or address (i.e., different from the Buyer's details) */}
              <Tabs.Content value="importAndExport">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold">
                      Import And Export Information
                    </h3>
                    <h5 className="text-sm font-light">
                      Applicable to import and export of goods
                    </h5>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="customs.formReferenceNumber1">
                        Reference Number of Customs Form No.1,9 etc.
                      </Label>
                      <Input
                        disabled={documentType === "01A"}
                        {...register("customs.formReferenceNumber1")}
                      />
                    </div>
                    <div>
                      <Label htmlFor="customs.incoterms">Incoterms</Label>
                      <Input
                        disabled={documentType === "01A"}
                        {...register("customs.incoterms")}
                      />
                    </div>
                    <div>
                      <Label htmlFor="customs.ftaInformation">
                        Free Trade Agreement (FTA) Information
                      </Label>
                      <Input
                        disabled={documentType === "01A"}
                        {...register("customs.ftaInformation")}
                      />
                    </div>
                    <div>
                      <Label htmlFor="customs.authorisationNumberForCertifiedExporter">
                        Authorization Number for Certified Exporter (e.g., ATIGA
                        number)
                      </Label>
                      <Input
                        disabled={documentType === "01A"}
                        {...register(
                          "customs.authorisationNumberForCertifiedExporter",
                        )}
                      />
                    </div>
                    <div>
                      <Label htmlFor="customs.formReferenceNumber2">
                        Reference Number of Custom Form No.2
                      </Label>
                      <Input
                        disabled={documentType === "01A"}
                        {...register("customs.formReferenceNumber2")}
                      />
                    </div>
                    <div>
                      <Label htmlFor="customs.detailsOfOtherCharges">
                        Details of Other Charges
                      </Label>
                      <Input
                        disabled={documentType === "01A"}
                        {...register("customs.detailsOfOtherCharges")}
                      />
                    </div>
                  </div>
                </div>
              </Tabs.Content>
            </Box>
          </Tabs.Root>

          {/* Items Section */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Items</h3>
              {["01", "11", "01A"].includes(documentType) && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsSearchModalOpen(true)}
                  className="shadow"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {documentType !== "01A" ? "Add Item" : "Add Invoice"}
                </Button>
              )}
            </div>

            <SearchModal
              isOpen={isSearchModalOpen && documentType !== "01A"}
              onClose={() => setIsSearchModalOpen(false)}
              onSelectItem={handleSelectItem}
            />

            <SearchInvoiceModal
              isOpen={isSearchModalOpen && documentType === "01A"}
              onClose={() => setIsSearchModalOpen(false)}
              onSelectInvoice={(e) => {
                handleConsolidateInvoice(e);
              }}
            />

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-28">Item Code</TableHead>
                  <TableHead className="min-w-36">Classification</TableHead>
                  <TableHead className="min-w-36">Description</TableHead>
                  <TableHead className="min-w-36">Unit Price</TableHead>
                  <TableHead className="min-w-36">Quantity</TableHead>
                  <TableHead className="min-w-36">Tax Type</TableHead>
                  <TableHead className="min-w-36">Tax Rate</TableHead>
                  <TableHead className="min-w-36">Tax Amount</TableHead>
                  <TableHead className="min-w-36">
                    Details of Tax Exemption
                  </TableHead>
                  <TableHead className="min-w-36">
                    Amount Exempted from Tax
                  </TableHead>
                  {/* <TableHead className="min-w-36">Subtotal</TableHead> */}
                  {/* <TableHead className="min-w-36">Total Excluding Tax</TableHead> */}
                  <TableHead className="min-w-36">Measurement</TableHead>
                  <TableHead className="min-w-36">Discount Rate</TableHead>
                  {/* <TableHead className="min-w-36">Discount Amount</TableHead> */}
                  <TableHead className="min-w-36">Fee / Charge Rate</TableHead>
                  {/* <TableHead className="min-w-36">Fee / Charge Amount</TableHead> */}
                  <TableHead className="min-w-36">
                    Product Tariff Code
                  </TableHead>
                  <TableHead className="min-w-36">Country of Origin</TableHead>
                  <TableHead className="min-w-36">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {fields.map((field, index) => {
                  return (
                    <TableRow key={field.id}>
                      <TableCell className="text-sm font-mono text-muted-foreground whitespace-nowrap">
                        {items[index]?.codeNumber ||
                          items[index]?.itemCode ||
                          "—"}
                      </TableCell>
                      <TableCell>
                        <Controller
                          name={`items.${index}.classification`}
                          control={control}
                          render={({ field }) => (
                            <Select
                              value={field.value}
                              onValueChange={field.onChange}
                              disabled={documentType === "01A"}
                            >
                              <SelectTrigger className="w-64 text-left">
                                <SelectValue placeholder="-- Select Classification --" />
                              </SelectTrigger>
                              <SelectContent className="w-96">
                                {classificationCodes.map((code) => (
                                  <SelectItem key={code.Code} value={code.Code}>
                                    {code.Code} - {code.Description}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          className="w-72"
                          {...register(`items.${index}.description`)}
                          disabled={documentType === "01A"}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step={step}
                          {...register(`items.${index}.unitPrice`, {
                            valueAsNumber: true,
                          })}
                          onChange={(e) =>
                            handleCalculation(
                              index,
                              "unitPrice",
                              e.target.value,
                            )
                          }
                          disabled={documentType === "01A"}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="any"
                          {...register(`items.${index}.quantity`, {
                            valueAsNumber: true,
                          })}
                          className="w-20"
                          onChange={(e) =>
                            handleCalculation(index, "quantity", e.target.value)
                          }
                          disabled={documentType === "01A"}
                        />
                      </TableCell>
                      <TableCell>
                        <Controller
                          name={`items.${index}.taxType`}
                          control={control}
                          render={({ field }) => (
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                              disabled={documentType === "01A"}
                            >
                              <SelectTrigger className="text-left w-56">
                                <SelectValue placeholder="-- Select tax type --" />
                              </SelectTrigger>
                              <SelectContent className="">
                                {taxTypes.map((taxType) => (
                                  <SelectItem
                                    key={taxType.Code}
                                    value={taxType.Code}
                                  >
                                    {taxType.Code} - {taxType.Description}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          {...register(`items.${index}.taxRate`, {
                            valueAsNumber: true,
                          })}
                          onChange={(e) =>
                            handleCalculation(index, "taxRate", e.target.value)
                          }
                          disabled={documentType === "01A"}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          {...register(`items.${index}.taxAmount`, {
                            valueAsNumber: true,
                          })}
                          disabled={
                            items[index]?.isEditable == 0 ||
                            documentType === "01A"
                          }
                          onChange={(e) =>
                            handleCalculation(
                              index,
                              "taxAmount",
                              e.target.value,
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          {...register(`items.${index}.detailsOfTaxExemption`)}
                          disabled={documentType === "01A"}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          {...register(`items.${index}.amountExemptedFromTax`, {
                            valueAsNumber: true,
                          })}
                          disabled={documentType === "01A"}
                        />
                      </TableCell>
                      {/* <TableCell>
                        <Input
                          type="number"
                          {...register(`items.${index}.subtotal`)}
                          className="w-20"
                          disabled
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          {...register(`items.${index}.totalExcludingTax`)}
                          className="w-24"
                          disabled
                        />
                      </TableCell> */}
                      <TableCell>
                        <Controller
                          control={control}
                          name={`items.${index}.measurement`}
                          render={({ field }) => (
                            <UomCombobox
                              value={field.value}
                              onChange={field.onChange}
                              disabled={documentType === "01A"}
                            />
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step={RATE_INPUT_STEP}
                          {...register(`items.${index}.discountRate`, {
                            valueAsNumber: true,
                          })}
                          onChange={(e) =>
                            handleCalculation(
                              index,
                              "discountRate",
                              e.target.value,
                            )
                          }
                          className="w-20"
                          disabled={documentType === "01A"}
                        />
                      </TableCell>
                      {/* <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          {...register(`items.${index}.discountAmount`, { valueAsNumber: true })}
                          onChange={(e) => handleCalculation(index, "discountAmount", e.target.value)}
                          className="w-20"
                          disabled
                        />
                      </TableCell> */}
                      <TableCell>
                        <Input
                          type="number"
                          step={RATE_INPUT_STEP}
                          {...register(`items.${index}.feeRate`, {
                            valueAsNumber: true,
                          })}
                          onChange={(e) =>
                            handleCalculation(index, "feeRate", e.target.value)
                          }
                          className="w-20"
                          disabled={documentType === "01A"}
                        />
                      </TableCell>
                      {/* <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          disabled
                          {...register(`items.${index}.feeAmount`, { valueAsNumber: true })}
                          onChange={(e) => handleCalculation(index, "feeAmount", e.target.value)}
                          className="w-20"
                        />
                      </TableCell> */}
                      <TableCell>
                        <Input
                          {...register(`items.${index}.productTariffCode`)}
                          className="w-20"
                          disabled={documentType === "01A"}
                        />
                      </TableCell>
                      <TableCell>
                        <Controller
                          name={`items.${index}.countryOfOrigin`}
                          control={control}
                          render={({ field }) => (
                            <Select
                              value={field.value}
                              onValueChange={field.onChange}
                              disabled={documentType === "01A"}
                            >
                              <SelectTrigger className="w-52 text-left">
                                <SelectValue placeholder="-- Select Country --" />
                              </SelectTrigger>
                              <SelectContent>
                                {countries.map((country) => (
                                  <SelectItem
                                    key={country.Code}
                                    value={country.Code}
                                  >
                                    {country.Country}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                      </TableCell>
                      <TableCell className="">
                        <Input
                          type="number"
                          {...register(`items.${index}.total`)}
                          disabled
                          className="border-0"
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveItem(index)}
                          className="p-0 h-auto"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!["01", "11", "01A"].includes(documentType) && (
                  <TableRow>
                    <TableCell>
                      <Select
                        value={newLineItem.classification}
                        onValueChange={(e) =>
                          setNewLineItem((prev) => ({
                            ...prev,
                            classification: e,
                          }))
                        }
                      >
                        <SelectTrigger className="w-64 text-left">
                          <SelectValue placeholder="-- Select Classification --" />
                        </SelectTrigger>
                        <SelectContent className="w-96">
                          {classificationCodes.map((code) => (
                            <SelectItem key={code.Code} value={code.Code}>
                              {code.Code} - {code.Description}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input
                        className="w-72"
                        value={newLineItem.description}
                        onChange={(e) =>
                          setNewLineItem((prev) => ({
                            ...prev,
                            description: e.target.value,
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        value={newLineItem.unitPrice}
                        onChange={(e) =>
                          setNewLineItem((prev) => ({
                            ...prev,
                            unitPrice: Number(e.target.value),
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        className="w-20"
                        value={newLineItem.quantity}
                        onChange={(e) =>
                          setNewLineItem((prev) => ({
                            ...prev,
                            quantity: Number(e.target.value),
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        onValueChange={(e) =>
                          setNewLineItem((prev) => ({
                            ...prev,
                            taxType: e as
                              | "01"
                              | "02"
                              | "03"
                              | "04"
                              | "05"
                              | "06"
                              | "E",
                          }))
                        }
                        value={newLineItem.taxType}
                      >
                        <SelectTrigger className="text-left w-56">
                          <SelectValue placeholder="-- Select tax type --" />
                        </SelectTrigger>
                        <SelectContent className="">
                          {taxTypes.map((taxType) => (
                            <SelectItem key={taxType.Code} value={taxType.Code}>
                              {taxType.Code} - {taxType.Description}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        value={newLineItem.taxRate}
                        onChange={(e) =>
                          setNewLineItem((prev) => ({
                            ...prev,
                            taxRate: Number(e.target.value),
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={newLineItem.taxAmount}
                        disabled
                        onChange={(e) =>
                          setNewLineItem((prev) => ({
                            ...prev,
                            taxAmount: Number(e.target.value),
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={newLineItem.detailsOfTaxExemption ?? undefined}
                        onChange={(e) =>
                          setNewLineItem((prev) => ({
                            ...prev,
                            detailsOfTaxExemption: e.target.value,
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={newLineItem.amountExemptedFromTax ?? undefined}
                        onChange={(e) =>
                          setNewLineItem((prev) => ({
                            ...prev,
                            amountExemptedFromTax: Number(e.target.value),
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <UomCombobox
                        value={newLineItem.measurement}
                        onChange={(code) =>
                          setNewLineItem((prev) => ({
                            ...prev,
                            measurement: code,
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        value={newLineItem.discountRate ?? undefined}
                        onChange={(e) =>
                          setNewLineItem((prev) => ({
                            ...prev,
                            discountRate: Number(e.target.value),
                          }))
                        }
                        className="w-20"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        value={newLineItem.feeRate ?? undefined}
                        onChange={(e) =>
                          setNewLineItem((prev) => ({
                            ...prev,
                            feeRate: Number(e.target.value),
                          }))
                        }
                        className="w-20"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={newLineItem.productTariffCode ?? undefined}
                        onChange={(e) =>
                          setNewLineItem((prev) => ({
                            ...prev,
                            productTariffCode: e.target.value,
                          }))
                        }
                        className="w-20"
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        value={newLineItem.countryOfOrigin}
                        onValueChange={(e) =>
                          setNewLineItem((prev) => ({
                            ...prev,
                            countryOfOrigin: e,
                          }))
                        }
                      >
                        <SelectTrigger className="w-52 text-left">
                          <SelectValue placeholder="-- Select Country --" />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map((country) => (
                            <SelectItem key={country.Code} value={country.Code}>
                              {country.Country}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="">
                      <Input
                        value={newLineItem.total}
                        disabled
                        className="border-0"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleAddLineItem}
                        className="p-0 h-auto"
                      >
                        <Plus className="text-black dark:text-white" />
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {/* Totals */}
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Total Excluding Tax:</span>
                <span>
                  {
                    currencyCodes.find(
                      (currency) => currency.Code == currencyCode,
                    )?.Symbol
                  }{" "}
                  {formatTotalAmount(Number(totals?.totalExcludingTax || 0))}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Total Including Tax:</span>
                <span>
                  {
                    currencyCodes.find(
                      (currency) => currency.Code == currencyCode,
                    )?.Symbol
                  }{" "}
                  {formatTotalAmount(Number(totals?.totalIncludingTax || 0))}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Total Tax Amount:</span>
                <span>
                  {
                    currencyCodes.find(
                      (currency) => currency.Code == currencyCode,
                    )?.Symbol
                  }{" "}
                  {formatTotalAmount(Number(totals?.totalTaxAmount || 0))}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="content-center">
                  Invoice Additional Discount Amount:
                </span>
                <span>
                  <Input
                    className="text-right h-8"
                    disabled={documentType == "01A"}
                    {...register("invoiceAdditionalDiscountAmount", {
                      setValueAs: (value) => Number(value),
                    })}
                  />
                  {errors.invoiceAdditionalDiscountAmount && (
                    <p className="text-red-600 text-sm mt-1">
                      {errors.invoiceAdditionalDiscountAmount.message?.toString()}
                    </p>
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="content-center">
                  Invoice Additional Fee Amount:
                </span>
                <span>
                  <Input
                    className="text-right h-8"
                    disabled={documentType == "01A"}
                    {...register("invoiceAdditionalFeeAmount", {
                      setValueAs: (value) => Number(value),
                    })}
                  />
                  {errors.invoiceAdditionalFeeAmount && (
                    <p className="text-red-600 text-sm mt-1">
                      {errors.invoiceAdditionalFeeAmount.message?.toString()}
                    </p>
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="content-center">Rounding Amount:</span>
                <span>
                  <Input
                    className="text-right h-8"
                    disabled={documentType == "01A"}
                    {...register("roundingAmount", {
                      setValueAs: (value) => Number(value),
                    })}
                  />
                  {errors.roundingAmount && (
                    <p className="text-red-600 text-sm mt-1">
                      {errors.roundingAmount.message?.toString()}
                    </p>
                  )}
                </span>
              </div>
              {/* <div className="flex justify-between">
                <span>Invoice Additional Discount Description:</span>
                <span className="w-80">
                  <textarea
                    className={cn(
                      "text-right flex h-32 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-base ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-slate-950 placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:border-slate-800 dark:bg-slate-950 dark:ring-offset-slate-950 dark:file:text-slate-50 dark:placeholder:text-slate-400 dark:focus-visible:ring-slate-300",
                      {
                        "border-red-500 dark:border-red-500 focus-visible:ring-red-500 dark:focus-visible:ring-red-500":
                          !!errors?.invoiceAddtionalDiscountDescription,
                      }
                    )}
                  />
                </span>
              </div> */}
              <Separator />
              <div className="flex justify-between font-bold">
                <span>Total Payable Amount:</span>
                <span>
                  {
                    currencyCodes.find(
                      (currency) => currency.Code == currencyCode,
                    )?.Symbol
                  }{" "}
                  {formatTotalAmount(Number(totals?.totalPayableAmount || 0))}
                </span>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <Button
              type="submit"
              className="w-full md:w-auto"
              disabled={isCreatingInvoice}
            >
              {isCreatingInvoice ? (
                <>
                  <Loader2 size={10} className="animate-spin" />
                  Saving
                </>
              ) : (
                "Save Invoice"
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

const getBuyerIdForConsolidatedInvoice = (documentType: DocumentType) => {
  const { data: buyer } = useQuery({
    queryKey: ["consolidatedInvoiceBuyer", documentType],
    queryFn: async () => {
      const response = await api.get<z.infer<typeof buyerSchema>>(
        "/buyer/0195cc59-06be-7494-98df-a184ef05f30b",
      );
      return response.data;
    },
    enabled: documentType === DocumentType.CONSOLIDATED_INVOICE,
  });

  return buyer;
};

export default EInvoiceForm;
